import {
    createContext,
    PropsWithChildren,
    useContext,
    useMemo,
    useState,
} from 'react'
import { FedexCheckInput } from '@/dto/fedexCheck.input'
import { addressMax } from '@/src/modules/build-a-card/constants'
import { IFullWidthFields } from '@/src/modules/smarty/utilities'
import * as yup from 'yup'
import { ValidationError } from 'yup'

interface ISmartyFunctions {
    setAddress1: Function
    setAddress2: Function
    setCity: Function
    setState: Function
    setZip: Function
}

type TRecipientDeliveryState = {
    companyName: string
    setCompanyName: Function
    name: string
    setName: Function
    address1: string
    setAddress1: Function
    address2: string
    setAddress2: Function
    city: string
    setCity: Function
    state: string
    setState: Function
    zip: string
    setZip: Function
    delivery: string
    setDelivery: Function
    isFullAddress: boolean
    addressPayload: FedexCheckInput
    smartyFunctions: ISmartyFunctions
    addressFullWidthFields: IFullWidthFields[]
    formErrors: ValidationError[]
    setFormErrors: Function
    searchManually: boolean
    setSearchManually: Function
    selectError: string | null
    setSelectError: Function
}

export const RecipientDeliveryContext =
    createContext<TRecipientDeliveryState | null>(null)

export const useRecipientDeliveryContext = (): TRecipientDeliveryState => {
    const context = useContext(RecipientDeliveryContext)

    if (!context) {
        throw new Error(
            'Please use RecipientDeliveryContext in parent component'
        )
    }

    return context
}

export const RecipientDeliveryContextProvider = (props: PropsWithChildren) => {
    const [companyName, setCompanyName] = useState<string>('')
    const [name, setName] = useState<string>('')
    const [address1, setAddress1] = useState<string>('')
    const [address2, setAddress2] = useState<string>('')
    const [city, setCity] = useState<string>('')
    const [state, setState] = useState<string>('')
    const [zip, setZip] = useState<string>('')
    const [delivery, setDelivery] = useState<string>('') //TODO Default to 1st option
    const isFullAddress = Boolean(name && address1 && city && state && zip)
    const [searchManually, setSearchManually] = useState<boolean>(false)
    const [formErrors, setFormErrors] = useState<yup.ValidationError[]>([])
    const [selectError, setSelectError] = useState<string | null>(null)

    const addressPayload: FedexCheckInput = useMemo(
        () => ({
            companyName: companyName,
            shippingName: name,
            addressLine1: address1,
            addressLine2: address2,
            city: city,
            stateAbbreviation: state,
            zipCode: zip,
        }),
        [address1, address2, city, companyName, name, state, zip]
    )

    const smartyFunctions: ISmartyFunctions = useMemo(
        () => ({
            setAddress1: setAddress1,
            setAddress2: setAddress2,
            setCity: setCity,
            setState: setState,
            setZip: setZip,
        }),
        []
    )

    const addressFullWidthFields: IFullWidthFields[] = useMemo(
        () => [
            {
                id: 'address1',
                value: address1,
                placeholder: 'Address Line 1',
                label: 'Address Line 1',
                setField: setAddress1,
                maxLength: addressMax,
                autoComplete: 'address-line1',
            },
            {
                id: 'address2',
                value: address2,
                placeholder: 'Address Line 2',
                label: 'Address Line 2',
                setField: setAddress2,
                maxLength: addressMax,
                autoComplete: 'address-line2',
            },
            {
                id: 'city',
                value: city,
                placeholder: 'City',
                label: 'City',
                setField: setCity,
                maxLength: addressMax,
                autoComplete: 'address-level2',
            },
        ],
        [address1, address2, city]
    )

    const values = useMemo(
        () => ({
            name,
            address1,
            address2,
            city,
            state,
            zip,
            delivery,
            isFullAddress,
            companyName,
            addressPayload,
            smartyFunctions,
            addressFullWidthFields,
            formErrors,
            searchManually,
            selectError,
            setName,
            setAddress1,
            setAddress2,
            setCity,
            setState,
            setZip,
            setDelivery,
            setCompanyName,
            setFormErrors,
            setSearchManually,
            setSelectError,
        }),
        [
            address1,
            address2,
            addressPayload,
            city,
            companyName,
            delivery,
            formErrors,
            addressFullWidthFields,
            isFullAddress,
            name,
            smartyFunctions,
            state,
            zip,
            searchManually,
            selectError,
        ]
    )

    return (
        <RecipientDeliveryContext.Provider value={values}>
            {props.children}
        </RecipientDeliveryContext.Provider>
    )
}
